import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Section } from './Shared';
const pages = [{
  src: 'https://cdn.magicpatterns.com/uploads/poboo7F4pGyte441isaWkv/Screenshot_20260716-143224~2.png',
  alt: 'Página sobre antioxidantes y apoyo hepático real',
  label: 'Criterio clínico: apoyo hepático real',
  focus: 'Balance antioxidante y decisiones sostenibles para acompañar el hígado.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fT47tZNxigMBj18vdhzuNm/Screenshot_20260716-143310~2.png',
  alt: 'Página de nutrición funcional y alimentos hepatoprotectores',
  label: 'Nutrición funcional para el hígado',
  focus: 'Alimentos y criterios terapéuticos para reducir inflamación y mejorar la bilis.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/i6Lqt4ZMJe3VkBfeq64XCe/Screenshot_20260716-143402~2.png',
  alt: 'Página del botiquín herbolario argentino',
  label: 'Botiquín herbolario argentino',
  focus: 'Plantas nativas, usos tradicionales y recomendaciones prácticas.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/f9xWs4G4vUztvAZJBaqSQc/Screenshot_20260716-143427~2.png',
  alt: 'Página del protocolo de 21 días',
  label: 'Protocolo de 21 días',
  focus: 'Una hoja de ruta diaria para acompañar el progreso entre sesiones.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4qpfKJGzfBL5F4JGZZfUQ6/Screenshot_20260716-143533~2.png',
  alt: 'Página de escritura terapéutica y gestión emocional',
  label: 'Gestión emocional y escritura terapéutica',
  focus: 'Ejercicios guiados para integrar el trabajo emocional del hígado.'
}];
export function MaterialPreview() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const select = useCallback((next: number) => {
    setDirection(next >= index ? 1 : -1);
    setIndex(next);
  }, [index]);
  const previous = () => {
    setDirection(-1);
    setIndex(value => (value - 1 + pages.length) % pages.length);
  };
  const next = () => {
    setDirection(1);
    setIndex(value => (value + 1) % pages.length);
  };
  return <Section className="max-w-5xl" data-id="element-198" data-mp="3etm6afs0fwm0">
      <header className="text-center" data-id="element-199" data-mp="3jm70hietwbq2">
        <p className="inline-flex rounded-full border border-brand-green/20 bg-brand-cream px-4 py-2 text-xs font-bold tracking-[.14em] text-brand-green" data-id="element-200" data-mp="3jn8cpq7kz49d">
          VISTA PREVIA DEL SISTEMA
        </p>
        <h2 className="mt-5 font-display text-4xl font-bold text-brand-dark md:text-5xl" data-id="element-201" data-mp="2qyt7twi5y2br">
          Conocé el material por dentro
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-muted" data-id="element-202" data-mp="2vyfji1icfaig">
          Herramientas diseñadas para aportar claridad clínica, seguimiento y
          recursos aplicables en cada sesión.
        </p>
      </header>
      <div className="mx-auto mt-7 max-w-4xl overflow-hidden rounded-3xl border border-brand-green/20 bg-brand-cream p-2 shadow-[0_18px_40px_rgba(23,59,43,.12)] sm:mt-9 sm:p-5" data-id="element-203" data-mp="g685erli6xmo">
        <div className="relative flex min-h-[468px] items-center justify-center overflow-hidden rounded-2xl bg-brand-surfaceLight/65 px-3 pb-16 pt-3 sm:min-h-[620px] sm:px-16 sm:py-8" data-id="element-204" data-mp="24oujqqwzm6e3">
          <AnimatePresence mode="wait" custom={direction} data-id="element-205" data-mp="1vwsnz5xv5ea5">
            <motion.img key={pages[index].src} src={pages[index].src} alt={pages[index].alt} initial={{
            opacity: 0,
            x: direction * 25
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -25
          }} transition={{
            duration: 0.25,
            ease: 'easeOut'
          }} className="max-h-[430px] w-auto max-w-full rounded-md border border-brand-green/10 object-contain shadow-lg sm:max-h-[560px]" draggable={false} data-id="element-206" data-mp="1vv7r39o2tj94" />
          </AnimatePresence>
          <button type="button" onClick={previous} aria-label="Ver material anterior" className="absolute bottom-3 left-4 grid h-11 w-11 place-items-center rounded-full border border-brand-green/20 bg-brand-cream text-brand-green shadow-sm transition-colors hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-brand-yellow sm:bottom-auto sm:left-3 sm:top-1/2 sm:-translate-y-1/2" data-id="element-207" data-mp="37c7i81yots1k">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-208" data-mp="1q1sg7hw0q2tt" />
          </button>
          <button type="button" onClick={next} aria-label="Ver material siguiente" className="absolute bottom-3 right-4 grid h-11 w-11 place-items-center rounded-full border border-brand-green/20 bg-brand-cream text-brand-green shadow-sm transition-colors hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-brand-yellow sm:bottom-auto sm:right-3 sm:top-1/2 sm:-translate-y-1/2" data-id="element-209" data-mp="1q3lo10idat3e">
            <ChevronRightIcon className="h-5 w-5" data-id="element-210" data-mp="178y8n90q5czm" />
          </button>
        </div>
        <div className="px-3 pb-3 pt-4 text-center sm:pb-2 sm:pt-5" data-id="element-211" data-mp="1bcmjc0knkop3">
          <h3 className="font-serif text-lg font-bold leading-tight text-brand-dark sm:text-xl" data-id="element-212" data-mp="xdwiqaymk6vz">
            {pages[index].label}
          </h3>
          <p className="mx-auto mt-1 max-w-xl text-xs leading-relaxed text-brand-muted sm:text-sm" data-id="element-213" data-mp="12div6i6mpj3q">
            {pages[index].focus}
          </p>
        </div>
      </div>
      <div className="mt-5 flex justify-center gap-2" data-id="element-214" data-mp="dscvcsrjteym">
        {pages.map((page, itemIndex) => <button key={page.label} type="button" aria-label={`Ver muestra: ${page.label}`} aria-current={index === itemIndex ? 'true' : undefined} onClick={() => select(itemIndex)} className={`h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-brand-yellow ${index === itemIndex ? 'w-8 bg-brand-green' : 'w-2.5 bg-brand-green/25 hover:bg-brand-green/50'}`} data-id="element-215" data-mp="sou2f7spg7fd" data-mpi={itemIndex} />)}
      </div>
    </Section>;
}